import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/auth';
import { FeatureFlagProvider } from '../featureFlag/FeatureFlagProvider';
import { PageContainer } from '../layout/PageContainer';
import { Card, P } from '../ui';

const DISABLE_AUTH_SYNC_KEY = 'hopsa_disable_auth_sync';

const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'hopsa_access_token',
  REFRESH_TOKEN: 'hopsa_refresh_token',
  ACCESS_TOKEN_EXPIRES: 'hopsa_access_token_expires',
  REFRESH_TOKEN_EXPIRES: 'hopsa_refresh_token_expires',
} as const;

const AUTH_STORAGE_KEY_SET = new Set<string>(Object.values(AUTH_STORAGE_KEYS));

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const initializeAuth = useAuthStore.use.initializeAuth();
  const isInitializing = useAuthStore.use.isInitializing();
  const isAuthenticated = useAuthStore.use.isAuthenticated();
  const silentRefresh = useAuthStore.use.silentRefresh();
  const error = useAuthStore.use.error();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track if we're on the client to avoid hydration mismatch
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Initialize authentication state on app startup (client-side only)
    if (isClient) {
      const pathname = window.location.pathname;
      const isAuthFlowPage =
        pathname.includes('/auth/activate') ||
        pathname.includes('/auth/verify') ||
        pathname.includes('/callback');

      if (!isAuthFlowPage) {
        sessionStorage.removeItem(DISABLE_AUTH_SYNC_KEY);
      }

      // Skip full initialization on auth flow pages —
      // these pages handle their own authentication
      if (isAuthFlowPage) {
        useAuthStore.getState().setInitializing(false);
        return;
      }

      initializeAuth();
    }
  }, [initializeAuth, isClient]);

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (!event.key) {
        return;
      }

      if (!AUTH_STORAGE_KEY_SET.has(event.key)) {
        return;
      }

      if (sessionStorage.getItem(DISABLE_AUTH_SYNC_KEY) === 'true') {
        const pathname = window.location.pathname;
        const isAuthFlowPage =
          pathname.includes('/auth/activate') ||
          pathname.includes('/auth/verify') ||
          pathname.includes('/callback');
        if (!isAuthFlowPage) {
          sessionStorage.removeItem(DISABLE_AUTH_SYNC_KEY);
        } else {
          return;
        }
      }

      if (sessionStorage.getItem(DISABLE_AUTH_SYNC_KEY) === 'true') {
        return;
      }

      initializeAuth();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [initializeAuth, isClient]);

  // Reconcile auth state when a request definitively fails authentication.
  // The API layer dispatches 'hopsa:unauthorized' after a failed token refresh,
  // so the header can't keep showing a logged-in user for a dead session.
  useEffect(() => {
    if (!isClient) {
      return;
    }

    const handleUnauthorized = () => {
      useAuthStore.getState().handleSessionExpired();
    };

    window.addEventListener('hopsa:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('hopsa:unauthorized', handleUnauthorized);
    };
  }, [isClient]);

  // Set up periodic token refresh for authenticated users
  useEffect(() => {
    if (isAuthenticated && !isInitializing) {
      // Set up interval to refresh tokens every 10 minutes
      intervalRef.current = setInterval(
        () => {
          silentRefresh();
        },
        10 * 60 * 1000
      ); // 10 minutes

      // Initial refresh attempt
      silentRefresh();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // Clear interval if user is not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isAuthenticated, isInitializing, silentRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Show loading state while auth is initializing (only on client after hydration)
  // On server and initial client render, we render children to avoid hydration mismatch
  if (isClient && isInitializing) {
    return (
      <PageContainer hideLogo hideHeader>
        <div className='flex items-center justify-center flex-1 min-h-[calc(100dvh-96px)]'>
          <Card glassmorphism className='text-center py-8 px-4'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto' />
            <div className='space-y-2'>
              <P className='text-gray-800 font-medium'>
                {t('auth.initializing.title')}
              </P>
              <P className='text-gray-600 text-sm'>
                {error
                  ? t('auth.initializing.retrying')
                  : t('auth.initializing.checking')}
              </P>
            </div>
            {error && (
              <div className='mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-w-sm mx-auto'>
                <P className='text-yellow-800 text-sm'>
                  {t('auth.initializing.connectionIssues')}
                </P>
              </div>
            )}
          </Card>
        </div>
      </PageContainer>
    );
  }

  return <FeatureFlagProvider>{children}</FeatureFlagProvider>;
};
